import React, { useState, useEffect } from 'react';
import { X, Share2, Users, AlertCircle } from 'lucide-react';
import api from '../../api/client';

export default function TaskShareModal({
  isOpen,
  onClose,
  taskId,
  taskTitle,
}) {
  const [targetType, setTargetType] = useState('USER');
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [permission, setPermission] = useState('VIEW');
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedUserId('');
      setSelectedTeamId('');
      setPermission('VIEW');
      setExpiresAt('');
      setError('');
      setSuccessMsg('');
      fetchRecipients();
    }
  }, [isOpen]);

  const fetchRecipients = async () => {
    try {
      const [uRes, tRes] = await Promise.all([
        api.get('/users/organization').catch(() => ({ data: [] })),
        api.get('/teams').catch(() => ({ data: [] })),
      ]);
      setUsers(uRes.data || []);
      setTeams(tRes.data || []);
    } catch (err) {
      // ignore
    }
  };

  if (!isOpen) return null;

  const handleShare = async (e) => {
    e.preventDefault();
    if (targetType === 'USER' && !selectedUserId) {
      setError('Please select a user to share with');
      return;
    }
    if (targetType === 'TEAM' && !selectedTeamId) {
      setError('Please select a team to share with');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      await api.post(`/tasks/${taskId}/share`, {
        user_id: targetType === 'USER' ? selectedUserId : undefined,
        team_id: targetType === 'TEAM' ? selectedTeamId : undefined,
        permission,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      });

      setSuccessMsg('Task shared successfully!');
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to share task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div
        className="modal-content glass-panel"
        style={{
          maxWidth: '480px',
          width: '95%',
          borderRadius: '16px',
          padding: 0,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--bg-surface)',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600, color: 'var(--text-primary)' }}>
              Share Task
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              "{taskTitle || 'Task'}"
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleShare} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {error && (
            <div style={{ padding: '10px', background: 'rgba(239, 68, 68, 0.15)', color: 'var(--danger)', borderRadius: '8px', fontSize: '13px' }}>
              {error}
            </div>
          )}
          {successMsg && (
            <div style={{ padding: '10px', background: 'rgba(16, 185, 129, 0.15)', color: 'var(--success)', borderRadius: '8px', fontSize: '13px' }}>
              {successMsg}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setTargetType('USER')}
              className={targetType === 'USER' ? 'btn-primary' : 'btn-secondary'}
              style={{ flex: 1, padding: '6px 12px', fontSize: '12px' }}
            >
              Share with User
            </button>
            <button
              type="button"
              onClick={() => setTargetType('TEAM')}
              className={targetType === 'TEAM' ? 'btn-primary' : 'btn-secondary'}
              style={{ flex: 1, padding: '6px 12px', fontSize: '12px' }}
            >
              Share with Team
            </button>
          </div>

          {targetType === 'USER' ? (
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>User</label>
              <select
                className="input-field"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Select recipient user...</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.email}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>Team</label>
              <select
                className="input-field"
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Select team...</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>Permission</label>
              <select
                className="input-field"
                value={permission}
                onChange={(e) => setPermission(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="VIEW">View Only</option>
                <option value="COMMENT">Can Comment</option>
                <option value="EDIT">Can Edit</option>
                <option value="MANAGE">Full Control</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>Expiration</label>
              <input
                type="datetime-local"
                className="input-field"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Sharing...' : 'Share Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
